import { create } from "zustand";
import { AxiosError } from "axios";
import {
  Answer,
  AssessmentData,
  AssessmentListItem,
  sendAssessmentAnswer,
  sendAssessmentById,
  finishAssessment,
  startAssessmentAttempt,
  getAssessmentAttempt,
  AssessmentAnswerResponse,
  AssessmentAnswerItem,
  AssessmentAnswerPayload,
  AssessmentAttemptStatus,
  AssessmentLeaderboardItem,
  AssessmentStatResponse,
  AssessmentStatSummary,
  getAssessmentStat,
} from "../axios/quiz";
import { checkSessionStatus } from "../axios/user";
import {
  isAxiosCanceledError,
  isAxiosNetworkError,
} from "../utils/internetConnection";
import {
  buildAssessmentAnswerRequest,
  normalizeAssessmentQuestion,
  type AssessmentSendAnswerOptions,
} from "../utils/assessmentInteractive";
import {
  normalizeAssessmentTimeLeft,
  normalizeAssessmentTotalTimeSpent,
} from "../utils/assessmentTimeLeft";
import { mapPreviousAnswersToDrafts } from "../utils/assessmentPreviousAnswers";
import {
  ASSESSMENT_PROTOCOL_VERSION,
  beginQuestionVisit,
  clearAssessmentSaveProtocol,
  clearPendingSavePayload,
  createAssessmentSaveId,
  createAssessmentSegmentId,
  enqueueQuestionSave,
  getQuestionRevision,
  getQuestionVisitId,
  rememberPendingSavePayload,
  setQuestionRevision,
} from "../utils/assessmentSaveProtocol";
import {
  resolveAssessmentAnswerEditingEnabled,
  resolveAssessmentGamificationEnabled,
  resolveAssessmentShowFeedback,
} from "../components/quiz-pop-up/assessment-contest-utils";

const STATS_OFFLINE_MESSAGE =
  "No internet connection. Contest statistics will refresh when you're back online.";
const STATS_SERVER_ERROR_MESSAGE = "Failed to load contest statistics.";
const STATS_NOT_FOUND_MESSAGE =
  "Contest not found. Statistics are unavailable for this contest.";

let assessmentStatFetchGeneration = 0;

// First unanswered question, or the last question if every draft is present.
const resumeQuestionIndexFromDrafts = (
  drafts: Record<number, unknown>,
  totalQuestions: number,
): number => {
  if (totalQuestions <= 0) {
    return 0;
  }

  for (let index = 0; index < totalQuestions; index += 1) {
    if (!drafts[index]) {
      return index;
    }
  }

  return totalQuestions - 1;
};

const resolveAssessmentStatsErrorMessage = (error: unknown): string => {
  if (
    !navigator.onLine ||
    isAxiosNetworkError(error) ||
    isAxiosCanceledError(error)
  ) {
    return STATS_OFFLINE_MESSAGE;
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const detailMessage =
      typeof detail === "string" && detail.trim()
        ? detail.trim()
        : Array.isArray(detail) &&
            typeof detail[0]?.msg === "string" &&
            detail[0].msg.trim()
          ? detail[0].msg.trim()
          : null;

    if (status === 404) {
      return detailMessage || STATS_NOT_FOUND_MESSAGE;
    }

    if (detailMessage) {
      return detailMessage;
    }
  }

  return STATS_SERVER_ERROR_MESSAGE;
};

const hasUsableAssessmentStats = (
  stats: AssessmentStatResponse | null | undefined,
): boolean => {
  if (!stats) {
    return false;
  }
  if (stats.assessment_finished === true) {
    return true;
  }
  if (Array.isArray(stats.leaderboard) && stats.leaderboard.length > 0) {
    return true;
  }
  if (Array.isArray(stats.concepts) && stats.concepts.length > 0) {
    return true;
  }
  return false;
};

const applyAssessmentTimeLeft = (
  assessmentData: AssessmentData | null,
  timeLeft: number | null | undefined
): AssessmentData | null => {
  if (!assessmentData) {
    return null;
  }

  if (typeof timeLeft !== "number" || !Number.isFinite(timeLeft)) {
    return assessmentData;
  }

  const normalizedTimeLeft = Math.max(0, timeLeft);

  return {
    ...assessmentData,
    time_left: normalizedTimeLeft,
  };
};

const normalizeLeaderboard = (leaderboard: unknown): AssessmentLeaderboardItem[] =>
  Array.isArray(leaderboard)
    ? leaderboard
        .filter((item): item is Record<string, unknown> => {
          if (!item || typeof item !== "object") {
            return false;
          }
          const row = item as Record<string, unknown>;
          const hasUserId = typeof row.user_id === "string";
          const hasName = typeof row.name === "string";
          const hasScore =
            typeof row.score === "number" || typeof row.user_score === "number";
          return hasUserId && hasName && hasScore;
        })
        .map((item) => {
          const userScore =
            typeof item.user_score === "number" ? item.user_score : undefined;
          const score =
            typeof item.score === "number"
              ? item.score
              : typeof userScore === "number"
                ? userScore
                : 0;
          const gamificationBonusScore =
            typeof item.gamification_bonus_score === "number"
              ? item.gamification_bonus_score
              : undefined;
          const baseItem = item as unknown as AssessmentLeaderboardItem;

          return {
            ...baseItem,
            score,
            user_score: userScore ?? score,
            mandatory_score:
              typeof item.mandatory_score === "number"
                ? item.mandatory_score
                : undefined,
            mandatory_max_score:
              typeof item.mandatory_max_score === "number"
                ? item.mandatory_max_score
                : undefined,
            bonus_question_score:
              typeof item.bonus_question_score === "number"
                ? item.bonus_question_score
                : undefined,
            bonus_question_max_score:
              typeof item.bonus_question_max_score === "number"
                ? item.bonus_question_max_score
                : undefined,
            gamification_bonus_score: gamificationBonusScore,
            contest_points:
              typeof item.contest_points === "number"
                ? item.contest_points
                : undefined,
            bonus_score:
              typeof item.bonus_score === "number"
                ? item.bonus_score
                : gamificationBonusScore,
          };
        })
    : [];

const pickScoreBreakdown = (
  responseObj: Record<string, unknown>,
): Partial<AssessmentData> => {
  const breakdown: Partial<AssessmentData> = {};
  const keys = [
    "mandatory_score",
    "mandatory_max_score",
    "bonus_question_score",
    "bonus_question_max_score",
    "user_score",
    "gamification_bonus_score",
    "contest_points",
  ] as const;

  for (const key of keys) {
    const value = responseObj[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      breakdown[key] = value;
    }
  }

  return breakdown;
};

const normalizeResponseAnswers = (
  value: unknown,
): AssessmentAnswerResponse["answers"] | undefined => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return undefined;
    }
    if (value.every((item) => typeof item === "string")) {
      return value as string[];
    }
    const answers = value
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }
        const raw = item as Record<string, unknown>;
        if (typeof raw.text !== "string") {
          return null;
        }
        if (typeof raw.points !== "number" || !Number.isFinite(raw.points)) {
          return null;
        }
        return { text: raw.text, points: raw.points };
      })
      .filter((item): item is Answer => item !== null);
    return answers.length > 0 ? answers : undefined;
  }

  if (value && typeof value === "object") {
    const map: Record<string, string> = {};
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      if (typeof raw === "string" && raw.trim()) {
        map[key] = raw;
      }
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }

  return undefined;
};

const resolveAnswerBonusScore = (
  responseObj: Record<string, unknown>,
  currentBonusScore: number,
): number => {
  if (typeof responseObj.gamification_bonus_score === "number") {
    return Math.max(0, responseObj.gamification_bonus_score - currentBonusScore);
  }
  if (typeof responseObj.bonus_score === "number") {
    return responseObj.bonus_score;
  }
  const speedBonus =
    typeof responseObj.speed_bonus === "number" ? responseObj.speed_bonus : 0;
  const streakBonus =
    typeof responseObj.streak_bonus === "number" ? responseObj.streak_bonus : 0;
  return speedBonus + streakBonus;
};

const extractAssessmentStatPatch = (
  responseObj: Record<string, unknown>,
): Partial<AssessmentStatResponse> => {
  const patch: Partial<AssessmentStatResponse> = {};
  const conceptKeys = [
    "strongest_concept",
    "needs_review_concept",
    "fastest_tap",
    "most_accurate",
    "best_comeback",
  ] as const;

  for (const key of conceptKeys) {
    const value = responseObj[key];
    if (
      value !== null &&
      value !== undefined &&
      (typeof value === "string" ||
        (typeof value === "object" && !Array.isArray(value)))
    ) {
      patch[key] = value as AssessmentStatResponse[typeof key];
    }
  }

  if (Array.isArray(responseObj.concepts)) {
    patch.concepts = responseObj.concepts as AssessmentStatSummary[];
  }

  if (typeof responseObj.assessment_finished === "boolean") {
    patch.assessment_finished = responseObj.assessment_finished;
  }

  return patch;
};

export type AssessmentDraftAnswer = {
  answer: AssessmentAnswerItem;
  options?: AssessmentSendAnswerOptions;
  timeSpent: number;
  finalized?: boolean;
};

export type SubmitAllDraftAnswersResult = {
  ok: boolean;
  failedIndices: number[];
};

export type FinishAssessmentSessionResult = {
  ok: boolean;
  conflict?: boolean;
  failedIndices?: number[];
  message?: string;
};

interface AssessmentStore {
  assessmentData: AssessmentData | null;
  assessmentStats: AssessmentStatResponse | null;
  loading: boolean;
  error: string | null;
  statsError: string | null;
  currentQuestionIndex: number;
  scoresEarned: number;
  assessmentId: string | null;
  availableAssessments: AssessmentListItem[];
  selectedContestTitle: string | null;
  isAssessmentListOpen: boolean;
  assessmentListLoading: boolean;
  answers: AssessmentAnswerItem[];
  draftAnswers: Record<number, AssessmentDraftAnswer>;
  questionStartTime: number | null;
  currentAnswerResponse: AssessmentAnswerResponse | null;
  fetchAssessment: () => Promise<void>;
  fetchAssessmentStat: () => Promise<boolean>;
  sendAnswer: (
    answer: AssessmentAnswerItem,
    options?: AssessmentSendAnswerOptions & {
      timeSpent?: number;
      suppressFeedback?: boolean;
      isSubmitted?: boolean;
    },
  ) => Promise<boolean>;
  upsertDraftAnswer: (draft: AssessmentDraftAnswer) => void;
  getDraftAnswer: (questionIndex: number) => AssessmentDraftAnswer | null;
  clearDrafts: () => void;
  submitAllDraftAnswers: (opts?: {
    timeLeft?: number | null;
    onlyIndices?: number[];
    clearOnSuccess?: boolean;
  }) => Promise<SubmitAllDraftAnswersResult>;
  finishAssessmentSession: (opts?: {
    timeLeft?: number | null;
  }) => Promise<FinishAssessmentSessionResult>;
  finalizeEditModeAssessment: (opts?: {
    timeLeft?: number | null;
  }) => Promise<FinishAssessmentSessionResult>;
  /** Protocol v2: start personal clock on Start Challenge (idempotent). */
  startAssessmentSession: () => Promise<{
    ok: boolean;
    status?: AssessmentAttemptStatus;
    conflict?: boolean;
    message?: string;
  }>;
  // Protocol v2: reload/timeout canonical attempt status (no finish).
  syncAssessmentAttempt: (opts?: {
    // When false, only refresh clock/state — do not rewrite local drafts.
    reconcileAnswers?: boolean;
  }) => Promise<{
    ok: boolean;
    status?: AssessmentAttemptStatus;
    message?: string;
  }>;
  setAssessmentData: (data: AssessmentData) => void;
  setAvailableAssessments: (assessments: AssessmentListItem[]) => void;
  closeAssessmentList: () => void;
  requestAssessmentSend: (assessmentId: string) => Promise<boolean>;
  clearStore: () => void;
  setCurrentQuestionIndex: (index: number) => void;
  addScore: (points: number) => void;
  setError: (message: string | null) => void;
  clearStatsError: () => void;
  sendAnswerWithZeroTime: () => Promise<void>;
}

export const useAssessmentStore = create<AssessmentStore>((set, get) => ({
  assessmentData: null,
  assessmentStats: null,
  loading: false,
  error: null,
  statsError: null,
  currentQuestionIndex: 0,
  scoresEarned: 0,
  assessmentId: null,
  availableAssessments: [],
  selectedContestTitle: null,
  isAssessmentListOpen: false,
  assessmentListLoading: false,
  answers: [],
  draftAnswers: {},
  questionStartTime: null,
  currentAnswerResponse: null,

  setError: (msg: string | null) =>
    set((state) => ({
      error: msg,
      loading: msg ? false : state.loading,
    })),

  fetchAssessment: async () => {
    const state = get();

    // If we already have assessment data, don't fetch again
    if (state.assessmentData && state.assessmentData.assessment_id) {
      set({ loading: false, error: null });
      return;
    }

    set({ loading: true, error: null });
    try {
      const { sendAssessmentAnswer } = await import("../axios/quiz");
      await sendAssessmentAnswer({
        assessment_id: "",
        answers: [],
      });

      const CHECK_TIMEOUT = 8000;
      const startedAt = Date.now();

      setTimeout(() => {
        const { assessmentData, loading } = get();
        if (
          !assessmentData &&
          loading &&
          Date.now() - startedAt >= CHECK_TIMEOUT
        ) {
          set({
            error: "Close the application on another devices",
            loading: false,
          });
        }
      }, CHECK_TIMEOUT);
    } catch (error: any) {
      console.error("Error triggering assessment fetch:", error);
      set({
        error: "Failed to load contest questions",
        loading: false,
      });
    }
  },

  fetchAssessmentStat: async () => {
    const requestId = ++assessmentStatFetchGeneration;

    try {
      const state = get();
      const assessmentId = state.assessmentData?.assessment_id;

      if (!assessmentId) {
        set({
          statsError: STATS_NOT_FOUND_MESSAGE,
        });
        return false;
      }

      const response = await getAssessmentStat(assessmentId);
      if (requestId !== assessmentStatFetchGeneration) {
        const latest = get();
        return (
          latest.statsError == null ||
          hasUsableAssessmentStats(latest.assessmentStats)
        );
      }

      const incomingLeaderboard = normalizeLeaderboard(response.leaderboard);
      const latest = get();
      const leaderboard =
        incomingLeaderboard.length > 0
          ? incomingLeaderboard
          : latest.assessmentStats?.leaderboard ?? [];
      const resolvedTimeLeft =
        response.assessment_finished === true ? 0 : response.time_left;

      set({
        statsError: null,
        assessmentStats: {
          ...response,
          leaderboard,
        },
        assessmentData: applyAssessmentTimeLeft(
          latest.assessmentData,
          resolvedTimeLeft,
        ),
      });
      return true;
    } catch (error) {
      if (requestId !== assessmentStatFetchGeneration) {
        const latest = get();
        return (
          latest.statsError == null &&
          hasUsableAssessmentStats(latest.assessmentStats)
        );
      }
      console.error("Error fetching assessment stats:", error);
      const latest = get();
      const locallyFinished =
        latest.assessmentData?.time_left === 0 ||
        latest.assessmentStats?.assessment_finished === true;
      if (hasUsableAssessmentStats(latest.assessmentStats) || locallyFinished) {
        set({ statsError: null });
        return true;
      }
      set({
        statsError: resolveAssessmentStatsErrorMessage(error),
      });
      return false;
    }
  },

  sendAnswer: async (
    answer: AssessmentAnswerItem,
    options?: AssessmentSendAnswerOptions & {
      timeSpent?: number;
      suppressFeedback?: boolean;
      isSubmitted?: boolean;
    },
  ): Promise<boolean> => {
    const assessmentId = get().assessmentData?.assessment_id;
    if (!assessmentId) {
      console.error(
        "No assessment_id found in assessmentData, cannot send answer",
      );
      return false;
    }

    const questionIndex = answer.question_index;

    return enqueueQuestionSave(assessmentId, questionIndex, async () => {
    try {
      // Check for device conflict before sending answer
      try {
        const sessionStatus = await checkSessionStatus();
        if (sessionStatus.status === "conflict") {
          set({
            error: "To continue the contest, please close it on your other device first.",
          });
          return false;
        }
      } catch (error) {
        console.error("Error checking session status before sending answer:", error);
        // Continue with sending answer if check fails
      }

      const state = get();

      // after_answer needs score/justification/answers for immediate UI feedback,
      // including when answer editing is enabled.
      const showFeedbackAfterAnswer =
        state.assessmentData?.show_feedback === "after_answer";
      const suppressFeedback =
        !showFeedbackAfterAnswer &&
        (options?.suppressFeedback === true ||
          state.assessmentData?.answer_editing_enabled === true);

      const currentScoresEarned =
        state.assessmentData?.scores_earned ?? state.scoresEarned;
      const answeredQuestionsNumber = state.currentQuestionIndex + 1;

      const timeLeft = state.assessmentData?.time_left ?? null;

      // Incremental segment seconds only — never a cumulative question total.
      const timeSpent =
        typeof options?.timeSpent === "number"
          ? Math.max(0, Math.floor(options.timeSpent))
          : state.questionStartTime
            ? Math.floor((Date.now() - state.questionStartTime) / 1000)
            : 0;

      const {
        timeSpent: _ignoredTimeSpent,
        suppressFeedback: _ignoredSuppress,
        isSubmitted: isSubmittedOption,
        ...sendOptions
      } = options ?? {};

      const isSubmitted =
        typeof isSubmittedOption === "boolean"
          ? isSubmittedOption
          : state.assessmentData?.answer_editing_enabled !== true;

      const questionId =
        state.assessmentData?.questions?.[questionIndex]?.id ??
        answer.question_id;
      const visitId = getQuestionVisitId(assessmentId, questionIndex);
      const saveId = createAssessmentSaveId();
      const segmentId = createAssessmentSegmentId();
      const expectedRevision = getQuestionRevision(assessmentId, questionIndex);

      const payload: AssessmentAnswerPayload = buildAssessmentAnswerRequest(
        assessmentId,
        answer,
        {
          total_questions_number: state.assessmentData?.total_questions_number,
          answered_questions_number: answeredQuestionsNumber,
          scores_earned:
            answer.score !== null
              ? currentScoresEarned + answer.score
              : currentScoresEarned,
          time_left: timeLeft,
          time_spent: timeSpent,
          is_submitted: isSubmitted,
          save_id: saveId,
          visit_id: visitId,
          segment_id: segmentId,
          expected_revision: expectedRevision,
          protocol_version: ASSESSMENT_PROTOCOL_VERSION,
          question_id: questionId,
        },
        sendOptions,
      );

      // Persist before send so lost responses can retry the exact same body.
      rememberPendingSavePayload(assessmentId, questionIndex, payload);

      let response: string | AssessmentAnswerResponse;
      try {
        response = await sendAssessmentAnswer(payload);
      } catch (firstError) {
        if (
          firstError instanceof AxiosError &&
          firstError.response?.status === 409
        ) {
          console.warn(
            "Assessment save conflict (409) — syncing attempt state",
            firstError,
          );
          await get().syncAssessmentAttempt();
          clearPendingSavePayload(assessmentId, questionIndex);
          return false;
        }

        // Network / lost response: retry the identical payload once.
        if (
          isAxiosNetworkError(firstError) ||
          (firstError instanceof AxiosError && !firstError.response)
        ) {
          response = await sendAssessmentAnswer(payload);
        } else {
          throw firstError;
        }
      }

      clearPendingSavePayload(assessmentId, questionIndex);

      const isReplayedAck =
        typeof response === "object" &&
        response !== null &&
        (response as AssessmentAnswerResponse).replayed === true;

      if (typeof response === "object" && response !== null) {
        const ack = response as AssessmentAnswerResponse;
        if (typeof ack.revision === "number") {
          setQuestionRevision(assessmentId, questionIndex, ack.revision);
        } else {
          setQuestionRevision(
            assessmentId,
            questionIndex,
            expectedRevision + 1,
          );
        }
        if (typeof ack.time_left === "number") {
          set({
            assessmentData: applyAssessmentTimeLeft(
              get().assessmentData,
              ack.time_left,
            ),
          });
        }
      }

      // Replay returns canonical state without repeating correctness feedback.
      if (isReplayedAck) {
        void get().fetchAssessmentStat();
        set({ questionStartTime: Date.now() });
        return true;
      }

      let answerResponse: AssessmentAnswerResponse | null = null;
      let updatedScoresEarned = currentScoresEarned;
      const currentBonusScore =
        state.assessmentData?.gamification_bonus_score ??
        state.assessmentData?.bonus_score ??
        0;
      let updatedBonusScore = currentBonusScore;
      let currentUserPlace = state.assessmentData?.current_user_place ?? null;
      let questionWinner: AssessmentAnswerResponse["question_winner"] = null;
      let streak: AssessmentAnswerResponse["streak"] = null;
      let scoreBreakdown: Partial<AssessmentData> = {};

      // Backend-evaluated answers: open_text and interactive question types.
      const needsBackendScore = answer.score === null;

      if (typeof response === "object" && response !== null) {
        const responseObj = response as Record<string, unknown>;
        const answerBonusScore = resolveAnswerBonusScore(
          responseObj,
          currentBonusScore,
        );
        const responseAnswers = normalizeResponseAnswers(responseObj.answers);
        scoreBreakdown = pickScoreBreakdown(responseObj);

        const resolveUpdatedBonusScore = (): number => {
          if (typeof responseObj.gamification_bonus_score === "number") {
            return responseObj.gamification_bonus_score;
          }
          if (typeof responseObj.bonus_score === "number") {
            return currentBonusScore + responseObj.bonus_score;
          }
          return currentBonusScore + answerBonusScore;
        };

        if (needsBackendScore) {
          // For open_text / interactive: expect {score: number, justification?: string}
          // Also MSQ when option points were withheld until after submit.
          if (
            "score" in responseObj &&
            typeof responseObj.score === "number" &&
            "justification" in responseObj &&
            typeof responseObj.justification === "string"
          ) {
            answerResponse = {
              score: responseObj.score,
              bonus_score: answerBonusScore,
              answers: responseAnswers,
              justification: responseObj.justification,
              ...scoreBreakdown,
            };
            updatedScoresEarned =
              typeof responseObj.user_score === "number"
                ? responseObj.user_score
                : currentScoresEarned + responseObj.score;
            updatedBonusScore = resolveUpdatedBonusScore();
          } else {
            // If justification is missing, still use score if available
            if (
              "score" in responseObj &&
              typeof responseObj.score === "number"
            ) {
              answerResponse = {
                score: responseObj.score,
                bonus_score: answerBonusScore,
                answers: responseAnswers,
                justification:
                  typeof responseObj.justification === "string"
                    ? responseObj.justification
                    : undefined,
                ...scoreBreakdown,
              };
              updatedScoresEarned =
                typeof responseObj.user_score === "number"
                  ? responseObj.user_score
                  : currentScoresEarned + responseObj.score;
              updatedBonusScore = resolveUpdatedBonusScore();
            }
          }
        } else {
          // For multiple_choice questions: expect {score: number} (optional)
          // We already have score from answer.score, but if backend returns score, use it
          if ("score" in responseObj && typeof responseObj.score === "number") {
            updatedScoresEarned =
              typeof responseObj.user_score === "number"
                ? responseObj.user_score
                : currentScoresEarned + responseObj.score;
            updatedBonusScore = resolveUpdatedBonusScore();
            answerResponse = {
              score: responseObj.score,
              bonus_score: answerBonusScore,
              answers: responseAnswers,
              ...scoreBreakdown,
            };
          } else if (answer.score !== null) {
            updatedScoresEarned =
              typeof responseObj.user_score === "number"
                ? responseObj.user_score
                : currentScoresEarned + answer.score;
            updatedBonusScore = resolveUpdatedBonusScore();
            answerResponse = {
              score: answer.score,
              bonus_score: answerBonusScore,
              answers: responseAnswers,
              ...scoreBreakdown,
            };
          }
        }

        if (
          "current_user_place" in responseObj &&
          typeof responseObj.current_user_place === "number"
        ) {
          currentUserPlace = responseObj.current_user_place;
        }


        if (
          responseObj.question_winner &&
          typeof responseObj.question_winner === "object" &&
          typeof (responseObj.question_winner as { user_id?: unknown }).user_id ===
            "string" &&
          typeof (responseObj.question_winner as { name?: unknown }).name ===
            "string" &&
          typeof (responseObj.question_winner as { time_spent?: unknown })
            .time_spent === "number"
        ) {
          questionWinner =
            responseObj.question_winner as AssessmentAnswerResponse["question_winner"];
        }

        if (typeof responseObj.streak === "number" && Number.isFinite(responseObj.streak)) {
          streak = responseObj.streak;
        } else if (
          typeof responseObj.current_streak === "number" &&
          Number.isFinite(responseObj.current_streak)
        ) {
          streak = responseObj.current_streak;
        } else if (
          responseObj.streak &&
          typeof responseObj.streak === "object" &&
          typeof (responseObj.streak as { correct_count?: unknown }).correct_count ===
            "number"
        ) {
          streak = (responseObj.streak as { correct_count: number }).correct_count;
        }

        if (
          answerResponse ||
          questionWinner ||
          streak !== null ||
          responseAnswers ||
          "score" in responseObj ||
          "bonus_score" in responseObj ||
          "gamification_bonus_score" in responseObj ||
          "justification" in responseObj
        ) {
          answerResponse = {
            ...(answerResponse ?? {}),
            ...scoreBreakdown,
            bonus_score:
              answerResponse?.bonus_score ?? answerBonusScore,
            answers: answerResponse?.answers ?? responseAnswers,
            question_winner: questionWinner,
            streak,
          };
        }
      } else {
        // Response is not an object, use score from answer (for multiple_choice)
        if (!needsBackendScore && answer.score !== null) {
          updatedScoresEarned = currentScoresEarned + answer.score;
        }
      }

      const responseTimeLeft =
        typeof response === "object" &&
        response !== null &&
        typeof (response as AssessmentAnswerResponse).time_left === "number"
          ? (response as AssessmentAnswerResponse).time_left
          : undefined;

      const statPatch =
        typeof response === "object" &&
        response !== null
          ? extractAssessmentStatPatch(response as Record<string, unknown>)
          : {};
      const resolvedTimeLeft = statPatch.assessment_finished
        ? 0
        : responseTimeLeft ?? timeLeft;

      if (suppressFeedback) {
        const nextScoresEarned =
          typeof scoreBreakdown.user_score === "number"
            ? scoreBreakdown.user_score
            : updatedScoresEarned;
        const nextBonusScore =
          typeof scoreBreakdown.gamification_bonus_score === "number"
            ? scoreBreakdown.gamification_bonus_score
            : updatedBonusScore;

        set({
          assessmentData: applyAssessmentTimeLeft(
            state.assessmentData
              ? {
                  ...state.assessmentData,
                  ...scoreBreakdown,
                  answered_questions_number: Math.max(
                    state.assessmentData.answered_questions_number ?? 0,
                    answeredQuestionsNumber,
                  ),
                  scores_earned: nextScoresEarned,
                  bonus_score: nextBonusScore,
                  user_score:
                    typeof scoreBreakdown.user_score === "number"
                      ? scoreBreakdown.user_score
                      : (state.assessmentData.user_score ?? nextScoresEarned),
                  gamification_bonus_score:
                    typeof scoreBreakdown.gamification_bonus_score === "number"
                      ? scoreBreakdown.gamification_bonus_score
                      : (state.assessmentData.gamification_bonus_score ??
                        nextBonusScore),
                }
              : null,
            resolvedTimeLeft,
          ),
          scoresEarned: nextScoresEarned,
          // Next viewing segment starts now (incremental time_spent).
          questionStartTime: Date.now(),
          currentAnswerResponse: null,
          assessmentStats: {
            ...state.assessmentStats,
            ...statPatch,
            leaderboard: state.assessmentStats?.leaderboard,
          },
        });
        return true;
      }

      set({
        assessmentData: applyAssessmentTimeLeft(
          state.assessmentData
            ? {
                ...state.assessmentData,
                ...scoreBreakdown,
                answered_questions_number: answeredQuestionsNumber,
                scores_earned:
                  typeof scoreBreakdown.user_score === "number"
                    ? scoreBreakdown.user_score
                    : updatedScoresEarned,
                bonus_score:
                  typeof scoreBreakdown.gamification_bonus_score === "number"
                    ? scoreBreakdown.gamification_bonus_score
                    : updatedBonusScore,
                current_user_place: currentUserPlace,
              }
            : null,
          resolvedTimeLeft,
        ),
        questionStartTime: Date.now(),
        currentAnswerResponse: answerResponse,
        assessmentStats: {
          ...state.assessmentStats,
          ...statPatch,
        },
      });

      // Refresh the single leaderboard source immediately after scoring.
      void get().fetchAssessmentStat();

      return true;
    } catch (error) {
      console.error("Error sending assessment answer:", error);
      if (error instanceof AxiosError && error.response?.status === 409) {
        await get().syncAssessmentAttempt();
      }
      return false;
    }
    });
  },

  upsertDraftAnswer: (draft) => {
    const questionIndex = draft.answer.question_index;
    set((state) => ({
      draftAnswers: {
        ...state.draftAnswers,
        [questionIndex]: draft,
      },
    }));
  },

  getDraftAnswer: (questionIndex) => {
    return get().draftAnswers[questionIndex] ?? null;
  },

  clearDrafts: () => {
    set({ draftAnswers: {} });
  },

  submitAllDraftAnswers: async (opts) => {
    const state = get();
    const assessmentId = state.assessmentData?.assessment_id;

    if (!assessmentId) {
      console.error(
        "No assessment_id found in assessmentData, cannot submit drafts",
      );
      return { ok: false, failedIndices: [] };
    }

    try {
      const sessionStatus = await checkSessionStatus();
      if (sessionStatus.status === "conflict") {
        set({
          error:
            "To continue the contest, please close it on your other device first.",
        });
        return { ok: false, failedIndices: Object.keys(state.draftAnswers).map(Number) };
      }
    } catch (error) {
      console.error(
        "Error checking session status before submitting drafts:",
        error,
      );
    }

    const onlyIndices = opts?.onlyIndices;
    const clearOnSuccess = opts?.clearOnSuccess !== false;
    // Locked answers are already on the server; re-POSTing them returns 403.
    const draftEntries = Object.values(state.draftAnswers).filter((draft) => {
      if (draft.finalized === true) {
        return false;
      }
      return onlyIndices
        ? onlyIndices.includes(draft.answer.question_index)
        : true;
    });

    if (draftEntries.length === 0) {
      // No answers to POST. Backend ignores empty answers + time_left: 0.
      if (typeof opts?.timeLeft === "number") {
        set({
          assessmentData: applyAssessmentTimeLeft(
            state.assessmentData,
            opts.timeLeft,
          ),
        });
      }
      return { ok: true, failedIndices: [] };
    }

    const timeLeft =
      typeof opts?.timeLeft === "number"
        ? opts.timeLeft
        : state.assessmentData?.time_left ?? null;
    const totalQuestions =
      state.assessmentData?.total_questions_number ?? draftEntries.length;

    const isAlreadySubmittedError = (error: unknown) => {
      if (!(error instanceof AxiosError)) {
        return false;
      }
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : typeof error.message === "string"
            ? error.message
            : "";
      return /already submitted/i.test(message);
    };

    const results = await Promise.allSettled(
      draftEntries.map(async (draft) => {
        const questionIndex = draft.answer.question_index;
        try {
          const questionId =
            state.assessmentData?.questions?.[questionIndex]?.id ??
            draft.answer.question_id;
          const payload = buildAssessmentAnswerRequest(
            assessmentId,
            draft.answer,
            {
              total_questions_number: totalQuestions,
              answered_questions_number: questionIndex + 1,
              scores_earned:
                draft.answer.score !== null ? draft.answer.score : 0,
              time_left: timeLeft,
              // Incremental banked segment for this draft flush.
              time_spent: Math.max(0, Math.floor(draft.timeSpent)),
              // Soft drafts only — /finish locks them collectively.
              is_submitted: false,
              save_id: createAssessmentSaveId(),
              visit_id: getQuestionVisitId(assessmentId, questionIndex),
              segment_id: createAssessmentSegmentId(),
              expected_revision: getQuestionRevision(
                assessmentId,
                questionIndex,
              ),
              protocol_version: ASSESSMENT_PROTOCOL_VERSION,
              question_id: questionId,
            },
            draft.options,
          );
          rememberPendingSavePayload(assessmentId, questionIndex, payload);
          const response = await sendAssessmentAnswer(payload);
          clearPendingSavePayload(assessmentId, questionIndex);
          if (typeof response === "object" && response !== null) {
            const ack = response as AssessmentAnswerResponse;
            if (typeof ack.revision === "number") {
              setQuestionRevision(assessmentId, questionIndex, ack.revision);
            }
          }
          return { questionIndex, draft, response };
        } catch (error) {
          // Safe to continue Finish if this soft answer was already locked.
          if (isAlreadySubmittedError(error)) {
            return {
              questionIndex,
              draft,
              response: null,
            };
          }
          throw error;
        }
      }),
    );

    const failedIndices: number[] = [];
    let totalScore = 0;
    let totalBonus = 0;
    let scoreBreakdown: Partial<AssessmentData> = {};
    let hasAuthoritativeBreakdown = false;
    let currentUserPlace = state.assessmentData?.current_user_place ?? null;
    let latestStatPatch: Partial<AssessmentStatResponse> = {};
    let resolvedTimeLeft = timeLeft;

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const draft = draftEntries[i];
      if (result.status === "rejected") {
        failedIndices.push(draft.answer.question_index);
        console.error(
          "Error submitting draft answer:",
          draft.answer.question_index,
          result.reason,
        );
        continue;
      }

      const { response } = result.value;
      const clientScore =
        typeof draft.answer.score === "number" ? draft.answer.score : 0;

      if (typeof response === "object" && response !== null) {
        const responseObj = response as AssessmentAnswerResponse &
          Record<string, unknown>;
        const responseBreakdown = pickScoreBreakdown(responseObj);
        if (Object.keys(responseBreakdown).length > 0) {
          scoreBreakdown = {
            ...scoreBreakdown,
            ...responseBreakdown,
          };
          hasAuthoritativeBreakdown = true;
        }

        const responseScore =
          typeof responseObj.score === "number" ? responseObj.score : clientScore;
        const responseBonus =
          typeof responseObj.gamification_bonus_score === "number"
            ? responseObj.gamification_bonus_score
            : typeof responseObj.bonus_score === "number"
              ? responseObj.bonus_score
              : 0;
        totalScore += responseScore;
        totalBonus += responseBonus;

        if (typeof responseObj.current_user_place === "number") {
          currentUserPlace = responseObj.current_user_place;
        }
        if (typeof responseObj.time_left === "number") {
          resolvedTimeLeft = responseObj.time_left;
        }
        latestStatPatch = {
          ...latestStatPatch,
          ...extractAssessmentStatPatch(responseObj),
        };
      } else {
        totalScore += clientScore;
      }
    }

    if (failedIndices.length > 0) {
      return { ok: false, failedIndices };
    }

    // Use submitted drafts count — store drafts may have been cleared by a
    // concurrent setAssessmentData (socket) during parallel POSTs.
    const submittedCount = draftEntries.length;
    const finalAnsweredCount =
      submittedCount >= totalQuestions
        ? Math.max(submittedCount, totalQuestions)
        : submittedCount;
    const finishedTimeLeft = latestStatPatch.assessment_finished
      ? 0
      : typeof opts?.timeLeft === "number"
        ? opts.timeLeft
        : resolvedTimeLeft;
    const resolvedTotalScore = hasAuthoritativeBreakdown
      ? (scoreBreakdown.user_score ?? totalScore)
      : totalScore;
    const resolvedTotalBonus = hasAuthoritativeBreakdown
      ? (scoreBreakdown.gamification_bonus_score ?? totalBonus)
      : totalBonus;

    set({
      assessmentData: applyAssessmentTimeLeft(
        state.assessmentData
          ? {
              ...state.assessmentData,
              ...scoreBreakdown,
              answered_questions_number: finalAnsweredCount,
              scores_earned: resolvedTotalScore,
              bonus_score: resolvedTotalBonus,
              current_user_place: currentUserPlace,
            }
          : null,
        finishedTimeLeft,
      ),
      scoresEarned: resolvedTotalScore,
      questionStartTime: clearOnSuccess ? null : state.questionStartTime,
      assessmentStats: {
        ...state.assessmentStats,
        ...latestStatPatch,
      },
      answers: draftEntries.map((draft) => draft.answer),
      draftAnswers: clearOnSuccess ? {} : state.draftAnswers,
    });

    void get().fetchAssessmentStat();

    return { ok: true, failedIndices: [] };
  },

  finishAssessmentSession: async (opts) => {
    const state = get();
    const assessmentId = state.assessmentData?.assessment_id;
    if (!assessmentId) {
      console.error(
        "No assessment_id found in assessmentData, cannot finish assessment",
      );
      return {
        ok: false,
        message: "Contest session is unavailable. Please reopen and try again.",
      };
    }

    const timeLeft =
      typeof opts?.timeLeft === "number"
        ? opts.timeLeft
        : state.assessmentData?.time_left ?? null;

    const submitResult = await get().submitAllDraftAnswers({
      timeLeft,
      clearOnSuccess: false,
    });
    if (!submitResult.ok) {
      return {
        ok: false,
        failedIndices: submitResult.failedIndices,
        message:
          "Could not save your answers. Check your connection and try again.",
      };
    }

    try {
      await finishAssessment(assessmentId);
      // User finished their attempt. Contest may still be live — keep time_left
      // unless this was a timer expiry (opts.timeLeft === 0).
      const preservedTimeLeft =
        typeof opts?.timeLeft === "number"
          ? opts.timeLeft
          : timeLeft;
      const totalQuestions =
        state.assessmentData?.total_questions_number ??
        state.assessmentData?.questions?.length ??
        0;

      set((latest) => ({
        assessmentData: applyAssessmentTimeLeft(
          latest.assessmentData
            ? {
                ...latest.assessmentData,
                answered_questions_number: Math.max(
                  latest.assessmentData.answered_questions_number ?? 0,
                  totalQuestions,
                ),
              }
            : null,
          preservedTimeLeft,
        ),
        questionStartTime: null,
        currentAnswerResponse: null,
        draftAnswers: {},
        // Do not set assessment_finished here — that flag means the contest
        // itself ended, not that this user clicked Finish.
      }));

      void get().fetchAssessmentStat();
      return { ok: true };
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const detail = error.response?.data?.detail;
        const message =
          typeof detail === "string" && detail.trim()
            ? detail.trim()
            : "Finish is blocked until every required answer is saved, including bonus questions.";
        return { ok: false, conflict: true, message };
      }

      console.error("Error finishing assessment session:", error);
      return {
        ok: false,
        message:
          "Could not finish the contest. Check your connection and try again.",
      };
    }
  },

  finalizeEditModeAssessment: async (opts) => {
    return get().finishAssessmentSession(opts);
  },

  clearStore: () => {
    assessmentStatFetchGeneration += 1;
    clearAssessmentSaveProtocol(get().assessmentData?.assessment_id);
    set({
      assessmentData: null,
      assessmentStats: null,
      currentQuestionIndex: 0,
      scoresEarned: 0,
      assessmentId: null,
      answers: [],
      draftAnswers: {},
      error: null,
      statsError: null,
      questionStartTime: null,
      currentAnswerResponse: null,
      availableAssessments: [],
      selectedContestTitle: null,
      isAssessmentListOpen: false,
      assessmentListLoading: false,
    });
  },

  setAvailableAssessments: (assessments: AssessmentListItem[]) => {
    set({
      availableAssessments: assessments,
      isAssessmentListOpen: assessments.length > 0,
      assessmentListLoading: false,
      error: null,
    });
  },

  closeAssessmentList: () => {
    set({
      availableAssessments: [],
      isAssessmentListOpen: false,
      assessmentListLoading: false,
    });
  },

  requestAssessmentSend: async (assessmentId: string) => {
    const trimmedId = assessmentId.trim();
    if (!trimmedId) {
      return false;
    }

    const selectedTitle =
      get().availableAssessments.find(
        (item) => item.assessment_id === trimmedId,
      )?.title ?? null;

    set({
      assessmentListLoading: true,
      error: null,
      selectedContestTitle: selectedTitle,
    });
    try {
      await sendAssessmentById(trimmedId);
      set({
        availableAssessments: [],
        isAssessmentListOpen: false,
        assessmentListLoading: false,
        error: null,
      });
      return true;
    } catch (error) {
      const detail =
        error instanceof AxiosError
          ? typeof error.response?.data?.detail === "string"
            ? error.response.data.detail
            : error.message
          : null;
      set({
        assessmentListLoading: false,
        selectedContestTitle: null,
        error: detail || "Failed to start the selected contest.",
      });
      return false;
    }
  },

  clearStatsError: () => set({ statsError: null }),

  setCurrentQuestionIndex: (index: number) => {
    const assessmentId = get().assessmentData?.assessment_id;
    if (assessmentId) {
      // Genuine revisit → new visit_id; revision is preserved.
      beginQuestionVisit(assessmentId, index);
    }
    set({
      currentQuestionIndex: index,
      questionStartTime: Date.now(),
      currentAnswerResponse: null,
    });
  },

  addScore: (points: number) =>
    set((state) => ({ scoresEarned: state.scoresEarned + points })),

  startAssessmentSession: async () => {
    const assessmentId = get().assessmentData?.assessment_id;
    if (!assessmentId) {
      return {
        ok: false,
        message: "Contest session is unavailable. Please reopen and try again.",
      };
    }

    try {
      const status = await startAssessmentAttempt(assessmentId);
      set((latest) => ({
        assessmentData: applyAssessmentTimeLeft(
          latest.assessmentData,
          status.time_left,
        ),
      }));

      if (Array.isArray(status.answers)) {
        for (const row of status.answers) {
          if (typeof row.revision === "number") {
            setQuestionRevision(assessmentId, row.question_index, row.revision);
          }
        }
      }

      if (status.state === "finished" || status.state === "expired") {
        return { ok: true, status };
      }

      return { ok: true, status };
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const detail = error.response?.data?.detail;
        return {
          ok: false,
          conflict: true,
          message:
            typeof detail === "string" && detail.trim()
              ? detail.trim()
              : "This attempt cannot be restarted. Please contact an operator.",
        };
      }
      console.error("Error starting assessment attempt:", error);
      return {
        ok: false,
        message:
          "Could not start the contest timer. Check your connection and try again.",
      };
    }
  },

  syncAssessmentAttempt: async (opts) => {
    const assessmentId = get().assessmentData?.assessment_id;
    if (!assessmentId) {
      return {
        ok: false,
        message: "Contest session is unavailable.",
      };
    }

    const reconcileAnswers = opts?.reconcileAnswers === true;

    try {
      const status = await getAssessmentAttempt(assessmentId);
      const state = get();

      if (Array.isArray(status.answers)) {
        for (const row of status.answers) {
          if (typeof row.revision === "number") {
            setQuestionRevision(assessmentId, row.question_index, row.revision);
          }
        }
      }

      if (reconcileAnswers && Array.isArray(status.answers)) {
        // Reconcile local drafts from canonical attempt answers (no scores).
        const nextDrafts = { ...state.draftAnswers };
        for (const row of status.answers) {
          const existing = nextDrafts[row.question_index];
          const userAnswer =
            typeof row.user_answer === "string" ? row.user_answer : "";
          nextDrafts[row.question_index] = {
            answer: {
              question_index: row.question_index,
              question_id: row.question_id,
              chosen_answer: userAnswer,
              concept: existing?.answer.concept ?? "",
              score: existing?.answer.score ?? null,
              all_score: existing?.answer.all_score ?? 0,
            },
            options: existing?.options,
            timeSpent:
              typeof row.time_spent === "number"
                ? row.time_spent
                : (existing?.timeSpent ?? 0),
            finalized: row.is_submitted === true,
          };
        }

        set({
          assessmentData: applyAssessmentTimeLeft(
            state.assessmentData,
            status.time_left,
          ),
          draftAnswers: nextDrafts,
        });
      } else {
        const currentTimeLeft = state.assessmentData?.time_left;
        if (currentTimeLeft !== status.time_left) {
          set({
            assessmentData: applyAssessmentTimeLeft(
              state.assessmentData,
              status.time_left,
            ),
          });
        }
      }

      return { ok: true, status };
    } catch (error) {
      console.error("Error syncing assessment attempt:", error);
      return {
        ok: false,
        message:
          "Could not refresh contest status. Check your connection and try again.",
      };
    }
  },

  sendAnswerWithZeroTime: async () => {
    const state = get();
    if (!state.assessmentData?.assessment_id) {
      console.error(
        "No assessment_id found in assessmentData, cannot mark timed out",
      );
      return;
    }

    // Timeout path: do not call /finish or repost expired drafts.
    await get().syncAssessmentAttempt();
  },

  setAssessmentData: (data: AssessmentData) => {
    const previousState = get();
    const previousAssessmentData = previousState.assessmentData;
    const pendingContestTitle = previousState.selectedContestTitle;
    set({
      error: null,
      availableAssessments: [],
      isAssessmentListOpen: false,
      assessmentListLoading: false,
    });

    const serverAnsweredCount = data.answered_questions_number ?? 0;
    const serverScoresEarned = data.scores_earned ?? 0;
    const serverBonusScore = data.bonus_score ?? 0;
    const totalQuestionsCount =
      data.total_questions_number ??
      (Array.isArray(data.questions) ? data.questions.length : 0);
    const serverCompleted =
      totalQuestionsCount > 0 && serverAnsweredCount >= totalQuestionsCount;

    const sameAssessment =
      Boolean(previousAssessmentData?.assessment_id) &&
      previousAssessmentData?.assessment_id === data.assessment_id;

    const payloadTitle =
      typeof data.title === "string" && data.title.trim()
        ? data.title.trim()
        : null;
    const resolvedTitle =
      payloadTitle ||
      (sameAssessment ? previousAssessmentData?.title?.trim() || null : null) ||
      (typeof pendingContestTitle === "string" && pendingContestTitle.trim()
        ? pendingContestTitle.trim()
        : null) ||
      undefined;

    const answerEditingEnabled = resolveAssessmentAnswerEditingEnabled(
      data,
      previousAssessmentData?.answer_editing_enabled === true,
    );

    // answered_questions_number is Firestore progress. 0 means a new/cleared
    // attempt — ignore leftover SQLite previous_answers in that case.
    const canResumeFromPreviousAnswers =
      answerEditingEnabled && !serverCompleted && serverAnsweredCount > 0;

    const memoryDrafts =
      sameAssessment && !serverCompleted && serverAnsweredCount > 0
        ? previousState.draftAnswers
        : {};
    const serverDrafts = canResumeFromPreviousAnswers
      ? mapPreviousAnswersToDrafts(data.previous_answers)
      : {};
    const preservedDrafts =
      Object.keys(memoryDrafts).length > 0 ? memoryDrafts : serverDrafts;
    const hasPreservedDrafts = Object.keys(preservedDrafts).length > 0;

    const isNewAttempt = serverAnsweredCount === 0;
    const timeLeft = normalizeAssessmentTimeLeft(data.time_left, data.time_limit, {
      allowTimeLimitFallback: isNewAttempt,
    });
    const totalTimeSpent = normalizeAssessmentTotalTimeSpent(data.total_time_spent);

    const resumeFromDraftsIndex = hasPreservedDrafts
      ? resumeQuestionIndexFromDrafts(preservedDrafts, totalQuestionsCount)
      : serverAnsweredCount;

    const currentQuestionIndex = serverCompleted
      ? Math.max(0, totalQuestionsCount - 1)
      : sameAssessment && hasPreservedDrafts
        ? previousState.currentQuestionIndex
        : answerEditingEnabled && hasPreservedDrafts
          ? resumeFromDraftsIndex
          : serverAnsweredCount;
    const scoresEarned = serverScoresEarned;
    const normalizedLeaderboard = normalizeLeaderboard(data.leaderboard);
    const previousStatsLeaderboard =
      previousState.assessmentStats?.leaderboard ?? [];
    // Opening payload may seed the board once; live updates come from `/assessment_stat`.
    const seededLeaderboard =
      normalizedLeaderboard.length > 0
        ? normalizedLeaderboard
        : sameAssessment && previousStatsLeaderboard.length > 0
          ? previousStatsLeaderboard
          : sameAssessment
            ? previousAssessmentData?.leaderboard ?? []
            : [];

    const nextAssessmentStats: AssessmentStatResponse = sameAssessment
      ? {
          ...previousState.assessmentStats,
          leaderboard:
            previousStatsLeaderboard.length > 0
              ? previousStatsLeaderboard
              : seededLeaderboard,
        }
      : {
          leaderboard: seededLeaderboard,
        };

    set({
      assessmentData: {
        ...data,
        title: resolvedTitle,
        questions: Array.isArray(data.questions)
          ? data.questions.map((question, index) =>
              normalizeAssessmentQuestion(
                question as unknown as Record<string, unknown>,
                index,
              ),
            )
          : data.questions,
        time_left: timeLeft ?? data.time_left ?? null,
        total_time_spent: totalTimeSpent,
        answered_questions_number: serverAnsweredCount,
        scores_earned: serverScoresEarned,
        bonus_score: serverBonusScore,
        leaderboard: seededLeaderboard,
        current_user_place:
          data.current_user_place ?? previousAssessmentData?.current_user_place ?? null,
        gamification_enabled: resolveAssessmentGamificationEnabled(
          data,
          previousAssessmentData?.gamification_enabled === true,
        ),
        answer_editing_enabled: answerEditingEnabled,
        show_feedback: resolveAssessmentShowFeedback(
          data,
          previousAssessmentData?.show_feedback,
        ),
        previous_answers: Array.isArray(data.previous_answers)
          ? data.previous_answers
          : undefined,
      },
      assessmentStats: nextAssessmentStats,
      assessmentId: data.assessment_id || null,
      selectedContestTitle: null,
      loading: false,
      error: null,
      currentQuestionIndex: currentQuestionIndex,
      scoresEarned: scoresEarned,
      answers: sameAssessment ? previousState.answers : [],
      draftAnswers: serverCompleted ? {} : preservedDrafts,
      questionStartTime: Date.now(),
      currentAnswerResponse: sameAssessment
        ? previousState.currentAnswerResponse
        : null,
    });
  },
}));
